import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  ogUrl?: string;
  article?: {
    publishedTime: string;
    author: string;
    tags: string[];
  };
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  ogUrl,
  article,
  structuredData
}: SEOProps) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com';
  const currentUrl = canonical || `${siteUrl}${location.pathname}`;
  const defaultOgImage = `${siteUrl}/og-image.jpg`;

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Open Graph tags
    updateMetaTag('og:title', ogTitle || title, true);
    updateMetaTag('og:description', ogDescription || description, true);
    updateMetaTag('og:url', ogUrl || currentUrl, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:image', ogImage || defaultOgImage, true);
    updateMetaTag('og:site_name', 'MtaPlannerPro', true);
    updateMetaTag('og:locale', 'en_AU', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', ogTitle || title);
    updateMetaTag('twitter:description', ogDescription || description);
    updateMetaTag('twitter:image', ogImage || defaultOgImage);

    // Article-specific tags
    if (article && ogType === 'article') {
      updateMetaTag('article:published_time', article.publishedTime, true);
      updateMetaTag('article:author', article.author, true);
      updateMetaTag('article:modified_time', new Date().toISOString(), true);
      
      // Remove old article tags first
      const oldTags = document.querySelectorAll('meta[property="article:tag"]');
      oldTags.forEach(tag => tag.remove());
      
      // Add new article tags
      article.tags.forEach(tag => {
        const tagElement = document.createElement('meta');
        tagElement.setAttribute('property', 'article:tag');
        tagElement.setAttribute('content', tag);
        document.head.appendChild(tagElement);
      });
    }

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = currentUrl;

    // Update last-modified
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toISOString());

    // Add/Update Structured Data
    if (structuredData) {
      // Remove old structured data script
      const oldScript = document.querySelector('script[data-schema="dynamic"]');
      if (oldScript) {
        oldScript.remove();
      }

      // Add new structured data
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'dynamic');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      if (article) {
        const articleTags = document.querySelectorAll('meta[property^="article:"]');
        articleTags.forEach(tag => tag.remove());
      }
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogType, ogUrl, currentUrl, article, structuredData, defaultOgImage, siteUrl]);
};

// Helper function to generate Schema.org structured data
export const generateSchemaOrg = (type: string, data: Record<string, unknown>) => {
  return {
    '@context': 'https://schema.org',
    '@type': type,
    ...data
  };
};

// Generate WebPage Schema
export const generateWebPageSchema = (page: {
  name: string;
  description: string;
  url: string;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com';
  
  return generateSchemaOrg('WebPage', {
    name: page.name,
    description: page.description,
    url: `${siteUrl}${page.url}`,
    inLanguage: 'en-AU',
    isPartOf: {
      '@type': 'WebSite',
      name: 'MtaPlannerPro',
      url: siteUrl
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: page.name,
          item: `${siteUrl}${page.url}`
        }
      ]
    }
  });
};

// Generate Article Schema
export const generateArticleSchema = (post: {
  title: string;
  description: string;
  author: { name: string; role: string };
  publishedAt: string;
  featuredImage: string;
  tags: string[];
  url: string;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com';
  
  return generateSchemaOrg('Article', {
    headline: post.title,
    description: post.description,
    image: post.featuredImage,
    datePublished: post.publishedAt,
    dateModified: new Date().toISOString(),
    author: {
      '@type': 'Person',
      name: post.author.name,
      jobTitle: post.author.role
    },
    publisher: {
      '@type': 'Organization',
      name: 'MtaPlannerPro',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/vite.svg`
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}${post.url}`
    },
    keywords: post.tags.join(', ')
  });
};

// Generate Blog Schema
export const generateBlogSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com';
  
  return generateSchemaOrg('Blog', {
    name: 'MtaPlannerPro Blog',
    description: 'Expert trade tips, industry insights, and business advice for Australian construction professionals',
    url: `${siteUrl}/blog`,
    inLanguage: 'en-AU',
    publisher: {
      '@type': 'Organization',
      name: 'MtaPlannerPro',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/vite.svg`
      }
    }
  });
};

// Generate Breadcrumb Schema
export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com';
  
  return generateSchemaOrg('BreadcrumbList', {
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`
    }))
  });
};

// Generate HowTo Schema
export const generateHowToSchema = (guide: {
  name: string;
  description: string;
  steps: Array<{ name: string; text: string; image?: string }>;
  totalTime?: string;
}) => {
  return generateSchemaOrg('HowTo', {
    name: guide.name,
    description: guide.description,
    totalTime: guide.totalTime || 'PT1M',
    step: guide.steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      ...(step.image && { image: step.image })
    }))
  });
};

// Generate Product Schema
export const generateProductSchema = (product: {
  name: string;
  description: string;
  image: string;
  price: string;
  currency: string;
  availability: string;
  rating?: { value: string; count: string };
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com';
  
  return generateSchemaOrg('Product', {
    name: product.name,
    description: product.description,
    image: product.image,
    brand: {
      '@type': 'Brand',
      name: 'MtaPlannerPro'
    },
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: product.currency,
      availability: `https://schema.org/${product.availability}`,
      url: siteUrl,
      priceValidUntil: '2026-12-31'
    },
    ...(product.rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: product.rating.value,
        ratingCount: product.rating.count,
        bestRating: '5',
        worstRating: '1'
      }
    })
  });
};

// Generate VideoObject Schema
export const generateVideoSchema = (video: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration: string;
  contentUrl: string;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com';
  
  return generateSchemaOrg('VideoObject', {
    name: video.name,
    description: video.description,
    thumbnailUrl: video.thumbnailUrl,
    uploadDate: video.uploadDate,
    duration: video.duration,
    contentUrl: video.contentUrl,
    embedUrl: video.contentUrl,
    publisher: {
      '@type': 'Organization',
      name: 'MtaPlannerPro',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/vite.svg`
      }
    }
  });
};

// Generate Local Business Schema
export const generateLocalBusinessSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com';
  
  return generateSchemaOrg('LocalBusiness', {
    name: 'MtaPlannerPro Pty Ltd',
    image: `${siteUrl}/vite.svg`,
    '@id': siteUrl,
    url: siteUrl,
    telephone: '+61-1300-123-456',
    email: 'support@mtaplannerpro.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Level 5, 123 Collins Street',
      addressLocality: 'Melbourne',
      addressRegion: 'VIC',
      postalCode: '3000',
      addressCountry: 'AU'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -37.8136,
      longitude: 144.9631
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '00:00',
      closes: '23:59'
    },
    priceRange: '$$',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '2619'
    }
  });
};
